import { RotateCcw, Calendar, Lock, LockOpen } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFilters } from '@/contexts/FilterContext';
import { useLiveDemoContext } from '@/contexts/LiveDemoContext';
import type { EnergyFilters } from '@/types/energy';
import { DATE_RANGE_OPTIONS } from '@/types/energy';

interface MobileMenuDateControlsProps {
  dateRange: EnergyFilters['dateRange'];
  onDateRangeChange: (value: EnergyFilters['dateRange']) => void;
  onResetFilters: () => void;
  dateRangeLocked?: boolean;
}

/**
 * Date range dropdown + reset control rendered in the mobile navigation menu header
 * (between the HT logo and the close button).
 *
 * Relocated here from the dashboard status bar so the status bar row no longer needs
 * to horizontally scroll on mobile. Reads filter/live-demo state directly from context
 * so the parent only needs to thread through the date range value and handlers.
 */
export function MobileMenuDateControls({
  dateRange,
  onDateRangeChange,
  onResetFilters,
  dateRangeLocked = false,
}: MobileMenuDateControlsProps) {
  const { setLiveDemoEnabled } = useLiveDemoContext();
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    companies,
    groups,
  } = useFilters();

  // Filters are "active" when anything differs from the default (all selected, 30d range)
  const hasActiveFilters =
    dateRange !== '30d' ||
    selectedCompanyIds.length !== companies.length ||
    selectedGroupIds.length !== groups.length ||
    selectedModels.length !== 3; // Default is all 3 model types

  return (
    <div className="flex items-center gap-1.5">
      {dateRangeLocked ? (
        <button
          onClick={() => setLiveDemoEnabled(false)}
          className="group relative flex items-center justify-between h-8 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors cursor-pointer w-[90px]"
        >
          <Lock className="h-3 w-3 text-[#8FB560] shrink-0 group-hover:hidden" />
          <LockOpen className="h-3 w-3 text-[#8FB560] shrink-0 hidden group-hover:block" />
          <span className="absolute inset-0 flex items-center justify-center group-hover:hidden">Today</span>
          <span className="absolute inset-0 flex items-center justify-center hidden group-hover:flex text-[#8FB560] font-medium text-[10px]">Disable</span>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50 shrink-0"><path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
        </button>
      ) : (
        <Select
          value={dateRange}
          onValueChange={(value) => onDateRangeChange(value as EnergyFilters['dateRange'])}
        >
          <SelectTrigger className="w-[90px] h-8 text-xs">
            <Calendar className="h-3 w-3 mr-1" />
            <SelectValue placeholder="Range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGE_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.shortLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {hasActiveFilters && (
        <button
          onClick={onResetFilters}
          className="flex items-center justify-center rounded hover:bg-accent transition-colors h-9 w-9 shrink-0"
          title="Reset filters"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
